// client/src/components/pages/LexiStar/PlanDispatch/DispatchPreviewDialog.tsx
//
// "Dispatch selected" preview (LS-09 §5.8, FR-TRIP-005; M3 T8-S01): the driver message for each
// selected trip, per language, before anything is sent. The message is rendered by the server
// from the tenant's DRIVER_TRIP_ASSIGNMENT template (POST /message-templates:preview,
// m3-contracts §7.2) with the same variables the rules evaluator gives it on
// TRIP.DISPATCH_REQUESTED (ST-00205: the API's dispatch request, from which the message is sent
// at once) — so what is shown is what the driver gets, reply buttons included. When the preview API is not
// available the standard wording is shown instead (and says so). Confirm dispatches each trip
// (POST /trips/:id:dispatch) and reports per trip.
//
// ST-00198: the recipient is who the notifications evaluator will message — the trip's driver,
// else the driver allocated to the truck now (GET /trips:resolveDriver) — named with the channel
// their register entry gives (preferred channel, opt-ins; "no opt-in on WhatsApp" when it says
// so), and the preview is rendered for that channel (SMS segments only on SMS). "Nobody will
// receive the message" only when no driver resolves. Converted to TypeScript.
//
// ST-00205: the driver is messaged the moment Dispatch is pressed, but the engine applies the
// dispatch — and tracking and stage updates start — only when the truck's tracker next reports.
// For a truck whose last position is older than the server's threshold (GET
// /trips/:id/last-report; 15 min unless configured) each trip shows a non-blocking warning that
// says so, with how long ago it last reported.
//
// ST-00249: a trip whose truck has a planned-maintenance or breakdown event over its planned start
// shows that too ("In the workshop: Brake service until 14:00"; workshopWarningFor, from the page's
// useWorkshopEvents) — a warning, the dispatch still goes ahead if confirmed.

import { useEffect, useRef, useState } from 'react';
import { FaTimes, FaPaperPlane, FaSatelliteDish, FaWrench } from 'react-icons/fa';
import { SegmentedControl } from '../../../../types/commonComponents';
import type {
  DriverChannel,
  DriverSource,
  RegisterDriverRow,
} from '../../../../types/lexistar-trip-drivers';
import { dispatchTrip } from '../../../../services/lexistarStatusBoard';
import { resolveTripDriver } from '../../../../services/lexistarTripDrivers';
import { tripLastReport } from '../../../../services/lexistarDispatch';
import type { TripLastReport } from '../../../../types/lexistar-dispatch';
import {
  previewTemplate as previewTemplateJs,
  renderedOf as renderedOfJs,
} from '../NotificationRules/notificationsApi';
import {
  LANGUAGES,
  channelBlockText,
  channelLabel,
  contactOfRegister,
  contactOfResolved,
  driverChannel,
  driverMessage,
  type ChannelPlan,
} from './planLogic';
import type { DriverContact } from './planTypes';

export const DISPATCH_TEMPLATE = 'DRIVER_TRIP_ASSIGNMENT';

type Language = (typeof LANGUAGES)[number]['value'];

/** A saved trip as the preview needs it (PlanDispatch builds it from the grid row). */
export interface PreviewTrip {
  id: string;
  reference: string | null;
  registration?: string;
  asset_id: string;
  driver_id: string | null;
  driver_name: string;
  driver_tag: string | null;
  driver_source: DriverSource | null;
  material: string;
  order_ref: string;
  from_name?: string | null;
  to_name?: string | null;
  route_id: string;
  route_code?: string | null;
  route_name?: string | null;
  budget_l: number | null;
  planned_km?: number | null;
  planned_start: Date | null;
}

/**
 * Who receives a trip's dispatch message, as the evaluator decides: the trip's driver ('trip'),
 * else the driver allocated to the truck at the dispatch ('allocation').
 */
export type Recipient =
  | { status: 'loading' }
  | { status: 'none' }
  | { status: 'error' }
  | {
      status: 'ok';
      name: string;
      /** The register entry (channel, opt-ins); null when the driver is not in the loaded register. */
      contact: DriverContact | null;
      via: 'trip' | 'allocation';
    };

interface Rendered {
  body: string;
  buttons: Array<{ id?: string | number; label: string }>;
  segments: { segments?: number | string } | null;
  providerStatus: string | null;
}

type Preview = { status: 'loading' } | { status: 'fallback' } | ({ status: 'ok' } & Rendered);

const previewTemplate = previewTemplateJs as unknown as (body: {
  key: string;
  channel: string;
  language: string;
  vars: Record<string, unknown>;
}) => Promise<unknown>;
const renderedOf = renderedOfJs as unknown as (res: unknown) => Rendered;

/**
 * Template variables for a planned trip, in the shape the evaluator gives the dispatch request
 * (TRIP.DISPATCH_REQUESTED, ST-00205) — the event the driver's message is sent from.
 */
export function dispatchVars(
  t: PreviewTrip,
  lang: Language,
  recipient: Recipient = { status: 'none' }
): Record<string, unknown> {
  const trip = {
    id: t.id,
    reference: t.reference,
    stage: 'DISPATCHED',
    from: t.from_name || null,
    to: t.to_name || null,
    planned_km: t.planned_km ?? null,
    planned_start: t.planned_start ? new Date(t.planned_start).toISOString() : null,
    budget_l: t.budget_l ?? null,
    material: t.material || null,
    registration: t.registration || null,
    route_id: t.route_id || null,
  };
  const driver =
    recipient.status === 'ok'
      ? {
          id: recipient.contact?.id ?? t.driver_id ?? null,
          name: recipient.name,
          tag: recipient.contact?.tag ?? t.driver_tag ?? null,
        }
      : { id: null, name: null, tag: null };
  return {
    event: { type: 'TRIP.DISPATCH_REQUESTED', category: 'TRIP', severity: 'I', trip_id: t.id },
    type: 'TRIP.DISPATCH_REQUESTED',
    category: 'TRIP',
    severity: 'I',
    trip,
    from: trip.from,
    to: trip.to,
    planned_km: trip.planned_km,
    planned_start: trip.planned_start,
    budget_l: trip.budget_l,
    registration: trip.registration,
    asset: trip.registration,
    route: { code: t.route_code || null, name: t.route_name || null },
    load: { material: t.material || null, order_ref: t.order_ref || null },
    driver,
    recipient: { kind: 'driver', name: driver.name },
    link: '[map link]',
    language: lang,
  };
}

/** The trip's recipient (see Recipient). */
async function recipientOf(
  t: PreviewTrip,
  driversById: Map<string, RegisterDriverRow>
): Promise<Recipient> {
  if (t.driver_id) {
    const d = driversById.get(t.driver_id);
    return {
      status: 'ok',
      name: t.driver_name || d?.name || t.driver_tag || 'Driver',
      contact: d ? contactOfRegister(d) : null,
      via: 'trip',
    };
  }
  if (!t.asset_id) return { status: 'none' };
  try {
    const r = await resolveTripDriver({ asset_id: t.asset_id });
    return r.driver_id
      ? { status: 'ok', name: r.driver_name, contact: contactOfResolved(r), via: 'allocation' }
      : { status: 'none' };
  } catch {
    return { status: 'error' };
  }
}

const planOf = (r: Recipient | undefined): ChannelPlan | null =>
  r && r.status === 'ok' && r.contact ? driverChannel(r.contact) : null;

/** The channel the message is previewed on: the recipient's, else the head of the default chain. */
const previewChannel = (r: Recipient): DriverChannel => {
  const plan = planOf(r);
  return plan?.channel || plan?.preferred || 'whatsapp';
};

/**
 * Per trip: the recipient; per trip × language: {status:'loading'|'ok'|'fallback', body, …}.
 * The register is read as it is when the dialog opens (the page loads it on arrival).
 */
function usePreviews(
  isOpen: boolean,
  trips: PreviewTrip[],
  driversById: Map<string, RegisterDriverRow>
): { recipients: Record<string, Recipient>; previews: Record<string, Preview> } {
  const [recipients, setRecipients] = useState<Record<string, Recipient>>({});
  const [previews, setPreviews] = useState<Record<string, Preview>>({});
  const register = useRef(driversById);
  register.current = driversById;
  useEffect(() => {
    if (!isOpen) {
      setRecipients((p) => (Object.keys(p).length ? {} : p));
      setPreviews((p) => (Object.keys(p).length ? {} : p));
      return undefined;
    }
    let alive = true;
    const initialRecipients: Record<string, Recipient> = {};
    const initialPreviews: Record<string, Preview> = {};
    trips.forEach((t) => {
      initialRecipients[t.id] = { status: 'loading' };
      LANGUAGES.forEach(({ value: lang }) => {
        initialPreviews[`${t.id}|${lang}`] = { status: 'loading' };
      });
    });
    setRecipients(initialRecipients);
    setPreviews(initialPreviews);
    const byId = register.current;
    trips.forEach((t) => {
      recipientOf(t, byId).then((recipient) => {
        if (!alive) return;
        setRecipients((p) => ({ ...p, [t.id]: recipient }));
        const channel = previewChannel(recipient);
        LANGUAGES.forEach(({ value: lang }) => {
          const key = `${t.id}|${lang}`;
          previewTemplate({
            key: DISPATCH_TEMPLATE,
            channel,
            language: lang,
            vars: dispatchVars(t, lang, recipient),
          })
            .then((res) => {
              const r = renderedOf(res);
              if (!alive) return;
              setPreviews((p) => ({
                ...p,
                [key]: r.body.trim() ? { status: 'ok', ...r } : { status: 'fallback' },
              }));
            })
            .catch(() => alive && setPreviews((p) => ({ ...p, [key]: { status: 'fallback' } })));
        });
      });
    });
    return () => {
      alive = false;
    };
  }, [isOpen, trips]);
  return { recipients, previews };
}

/** A trip's truck's last report while the dialog is open (ST-00205). */
type LastReportState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ok'; report: TripLastReport };

/** Per trip: the truck's last report (GET /trips/:id/last-report), read when the dialog opens. */
function useLastReports(isOpen: boolean, trips: PreviewTrip[]): Record<string, LastReportState> {
  const [reports, setReports] = useState<Record<string, LastReportState>>({});
  useEffect(() => {
    if (!isOpen) {
      setReports((p) => (Object.keys(p).length ? {} : p));
      return undefined;
    }
    let alive = true;
    setReports(Object.fromEntries(trips.map((t) => [t.id, { status: 'loading' } as LastReportState])));
    trips.forEach((t) => {
      tripLastReport(t.id)
        .then((report) => alive && setReports((p) => ({ ...p, [t.id]: { status: 'ok', report } })))
        .catch(() => alive && setReports((p) => ({ ...p, [t.id]: { status: 'error' } })));
    });
    return () => {
      alive = false;
    };
  }, [isOpen, trips]);
  return reports;
}

/** "70 min ago", "3 h 5 min ago", "2 days ago" from an age in seconds. */
export function reportAge(ageS: number): string {
  const min = Math.max(0, Math.round(ageS / 60));
  if (min < 1) return 'less than a minute ago';
  if (min < 120) return `${min} min ago`;
  const hours = Math.floor(min / 60);
  if (hours < 48) {
    const rest = min - hours * 60;
    return rest ? `${hours} h ${rest} min ago` : `${hours} h ago`;
  }
  return `${Math.floor(hours / 24)} days ago`;
}

/**
 * ST-00205: the warning for a truck whose tracker has not reported for longer than the
 * threshold — the driver is messaged now, tracking starts when the tracker next reports. null
 * for a fresh report (or while / when the report cannot be read: nothing is claimed then).
 */
export function trackerWarning(report: TripLastReport | null | undefined, registration?: string): string | null {
  if (!report || !report.stale) return null;
  const truck = registration || report.registration || 'the truck';
  const when =
    report.last_report_at && report.age_s !== null
      ? `last report ${reportAge(report.age_s)}`
      : 'no report in the last 2 days';
  return `The driver is messaged now, but tracking and stage updates start when ${truck}'s tracker next reports (${when}).`;
}

const capitalise = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

/** The notes under a trip's message about who receives it and on which channel. */
function recipientNotes(
  t: PreviewTrip,
  r: Recipient | undefined
): Array<{ text: string; warn: boolean }> {
  if (!r || r.status === 'loading') return [];
  if (r.status === 'none') {
    return [{ text: 'No driver on this trip — nobody will receive the message.', warn: true }];
  }
  if (r.status === 'error') {
    return [{ text: "The truck's allocated driver could not be looked up.", warn: true }];
  }
  const notes: Array<{ text: string; warn: boolean }> = [];
  if (r.via === 'allocation') {
    notes.push({
      text: `The trip names no driver: ${r.name}, allocated to ${t.registration || 'the truck'} now, receives it.`,
      warn: false,
    });
  }
  const plan = planOf(r);
  if (plan && plan.preferred && plan.preferredBlocked) {
    notes.push({
      text: `${capitalise(channelBlockText(plan.preferred, plan.preferredBlocked))} — ${
        plan.channel
          ? `the message goes on ${channelLabel(plan.channel)}`
          : 'no other channel reaches this driver, so nothing is sent'
      }.`,
      warn: true,
    });
  } else if (plan && !plan.channel) {
    notes.push({
      text: 'No channel reaches this driver (no phone number or Telegram chat on record) — nothing is sent.',
      warn: true,
    });
  }
  return notes;
}

export interface DispatchPreviewDialogProps {
  isOpen: boolean;
  trips: PreviewTrip[];
  /** The driver register (active drivers) by id: the recipients' channels and opt-ins. */
  driversById: Map<string, RegisterDriverRow>;
  onClose: () => void;
  onDone?: (ok: PreviewTrip[], failed: Array<{ trip: PreviewTrip; error: string }>) => void;
  /** ST-00249: the truck's maintenance / breakdown over the planned start, in words; else null. */
  workshopWarningFor?: (trip: PreviewTrip) => string | null;
}

export default function DispatchPreviewDialog({
  isOpen,
  trips,
  driversById,
  onClose,
  onDone,
  workshopWarningFor,
}: DispatchPreviewDialogProps) {
  const [lang, setLang] = useState<Language>('en');
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState<Record<string, { ok: boolean; error?: string }>>({});
  const { recipients, previews } = usePreviews(isOpen, trips, driversById);
  const lastReports = useLastReports(isOpen, trips);

  useEffect(() => {
    if (isOpen) {
      setLang('en');
      setResults({});
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const send = async () => {
    setSending(true);
    const out: Record<string, { ok: boolean; error?: string }> = {};
    for (const t of trips) {
      try {
        await dispatchTrip(t.id);
        out[t.id] = { ok: true };
      } catch (err) {
        const e = err as { response?: { data?: { error?: string } }; message?: string };
        out[t.id] = { ok: false, error: e.response?.data?.error || e.message || 'Failed' };
      }
    }
    setResults(out);
    setSending(false);
    const ok = trips.filter((t) => out[t.id]?.ok);
    onDone?.(
      ok,
      trips
        .filter((t) => !out[t.id]?.ok)
        .map((t) => ({ trip: t, error: out[t.id]?.error || 'Failed' }))
    );
    if (ok.length === trips.length) onClose();
  };

  const anyFallback = trips.some((t) => previews[`${t.id}|${lang}`]?.status === 'fallback');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Dispatch preview"
        className="bg-surface rounded-2xl shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] flex flex-col overflow-hidden"
        style={{ animation: 'fadeInScale 0.2s ease-out' }}
      >
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between gap-4">
          <div>
            <h3 className="text-lg font-bold text-slate-900">
              Dispatch {trips.length} trip{trips.length === 1 ? '' : 's'}
            </h3>
            <p className="text-sm text-slate-500">
              Each driver gets this message on their channel and is asked to accept.
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="text-slate-400 hover:text-slate-600"
          >
            <FaTimes size={18} />
          </button>
        </div>
        <div className="px-6 pt-4">
          <SegmentedControl<Language>
            options={LANGUAGES}
            value={lang}
            onChange={setLang}
            ariaLabel="Message language"
          />
        </div>
        <ul className="flex-1 overflow-y-auto p-6 space-y-4">
          {trips.map((t) => {
            const p: Preview = previews[`${t.id}|${lang}`] || { status: 'loading' };
            const r = recipients[t.id];
            const plan = planOf(r);
            const channel = r && r.status !== 'loading' ? previewChannel(r) : null;
            const who =
              !r || r.status === 'loading'
                ? 'finding the driver…'
                : r.status === 'ok'
                  ? r.name
                  : 'no driver';
            const channelText = plan
              ? plan.channel
                ? channelLabel(plan.channel)
                : 'no channel'
              : null;
            const waBlocked =
              p.status === 'ok' &&
              channel === 'whatsapp' &&
              !!p.providerStatus &&
              !['approved', 'n/a'].includes(String(p.providerStatus));
            return (
              <li key={t.id} aria-label={`Message for ${t.reference}`}>
                <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                  <span data-testid="preview-recipient">
                    <span className="font-mono font-semibold text-slate-800">{t.registration}</span>{' '}
                    · {who}
                    {channelText ? ` · ${channelText}` : ''}
                  </span>
                  {results[t.id] && (
                    <span className={results[t.id]?.ok ? 'text-emerald-700' : 'text-red-700'}>
                      {results[t.id]?.ok ? 'Dispatched' : results[t.id]?.error}
                    </span>
                  )}
                </div>
                <div
                  className="whitespace-pre-wrap font-sans text-sm text-slate-800 bg-slate-50 border border-slate-200 rounded-lg p-3"
                  data-preview-status={p.status}
                  data-testid="preview-message"
                >
                  {p.status === 'loading' ? (
                    <span className="text-slate-400 italic">Rendering the message…</span>
                  ) : p.status === 'ok' ? (
                    p.body
                  ) : (
                    driverMessage(t, lang)
                  )}
                  {p.status === 'ok' && p.buttons.length > 0 && (
                    <span className="mt-2 flex flex-wrap gap-1.5" aria-label="Reply buttons">
                      {p.buttons.map((b) => (
                        <span
                          key={b.id ?? b.label}
                          className="px-2.5 py-0.5 rounded-full bg-surface text-xs font-semibold text-primary-700 ring-1 ring-slate-200"
                        >
                          {b.label}
                        </span>
                      ))}
                    </span>
                  )}
                </div>
                {channel === 'sms' && p.status === 'ok' && p.segments && (
                  <p
                    className={`mt-1 text-xs ${Number(p.segments.segments) > 1 ? 'text-amber-800' : 'text-slate-500'}`}
                  >
                    {p.segments.segments} SMS segment{Number(p.segments.segments) === 1 ? '' : 's'}
                  </p>
                )}
                {waBlocked && (
                  <p className="mt-1 text-xs text-amber-800">
                    The WhatsApp template is “{p.providerStatus}” — until Meta approves it, this
                    message goes on the driver's next channel.
                  </p>
                )}
                {recipientNotes(t, r).map((n) => (
                  <p
                    key={n.text}
                    className={`mt-1 text-xs ${n.warn ? 'text-amber-800' : 'text-slate-500'}`}
                  >
                    {n.text}
                  </p>
                ))}
                {(() => {
                  const workshop = workshopWarningFor ? workshopWarningFor(t) : null;
                  return workshop ? (
                    <p
                      className="mt-1.5 flex items-start gap-1.5 rounded-md bg-amber-50 px-2 py-1.5 text-xs text-amber-800 ring-1 ring-inset ring-amber-200"
                      role="note"
                      data-testid="workshop-warning"
                    >
                      <FaWrench className="mt-0.5 flex-shrink-0" size={11} aria-hidden="true" />
                      <span>{workshop}</span>
                    </p>
                  ) : null;
                })()}
                {(() => {
                  const lr = lastReports[t.id];
                  const warning = lr && lr.status === 'ok' ? trackerWarning(lr.report, t.registration) : null;
                  return warning ? (
                    <p
                      className="mt-1.5 flex items-start gap-1.5 rounded-md bg-amber-50 px-2 py-1.5 text-xs text-amber-800 ring-1 ring-inset ring-amber-200"
                      role="note"
                      data-testid="tracker-warning"
                    >
                      <FaSatelliteDish className="mt-0.5 flex-shrink-0" size={11} aria-hidden="true" />
                      <span>{warning}</span>
                    </p>
                  ) : null;
                })()}
              </li>
            );
          })}
        </ul>
        <p className="px-6 text-xs text-slate-500">
          {anyFallback
            ? "The template preview is not available, so the standard wording is shown; the driver receives the tenant's template."
            : "Rendered from your organisation's message templates — this is what the driver receives."}
        </p>
        <div className="flex justify-end gap-3 px-6 py-4 mt-3 bg-slate-50 border-t border-slate-100">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-600 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            onClick={send}
            disabled={sending || trips.length === 0}
            className={`inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white rounded-lg shadow-sm ${sending ? 'bg-slate-400 cursor-not-allowed' : 'bg-primary-500 hover:bg-primary-600'}`}
          >
            <FaPaperPlane size={12} />
            {sending ? 'Dispatching...' : `Dispatch ${trips.length}`}
          </button>
        </div>
      </div>
    </div>
  );
}
